import { AuthProvider, UserIdentity } from "react-admin";
import { authApi, userApi } from "./env";

type LoginFormType = {
  email: string;
  password: string;
};

const logoutFn = async (): Promise<void> => {
  localStorage.removeItem("token");
  localStorage.removeItem("permissions");
  localStorage.removeItem("agentId");
  localStorage.removeItem("organization_id");
  localStorage.removeItem("agentName");
  localStorage.removeItem("is_superuser");
  localStorage.removeItem("super_admin_token");
  localStorage.removeItem("super_admin_permissions");
};

const extractStatus = (error: any): number | undefined => {
  // inspect many shapes
  if (!error) return undefined;
  if (typeof error === "number") return error;
  if (typeof error?.status === "number") return error.status;
  if (typeof error?.statusCode === "number") return error.statusCode;
  if (typeof error?.response?.status === "number") return error.response.status;
  if (typeof error?.response?.data?.status === "number")
    return error.response.data.status;
  // sometimes react-admin wraps in {message: ..., body: {...}}
  if (typeof error?.body?.status === "number") return error.body.status;
  return undefined;
};

const forceRedirectToLogin = (keepPath = false) => {
  // fallback redirect if RA doesn't redirect automatically
  const loginPath = "/login";
  if (keepPath) {
    const next = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    window.location.href = `${loginPath}?next=${next}`;
  } else {
    window.location.href = loginPath;
  }
};

const authProvider: AuthProvider = {
  login: async ({ email, password }: LoginFormType) => {
    const formData = { username: email, password };
    const resp = await authApi.authJwtLogin(formData);
    const token = resp?.data?.access_token;
    if (!token) {
      return Promise.reject(new Error("No access token returned"));
    }
    localStorage.setItem("token", token);

    try {
      // @ts-ignore
      await authProvider.getIdentity();

      return Promise.resolve();
    } catch (err) {
      await logoutFn();
      return Promise.reject(err);
    }
  },

  logout: async () => {
    await logoutFn();
    // return resolved promise — react-admin typically handles redirect, but also fallback:
    forceRedirectToLogin();
    return Promise.resolve();
  },

  checkError: async (error: any) => {
    // logging giúp debug shape lỗi
    // eslint-disable-next-line no-console
    console.debug("[authProvider.checkError] error:", error);

    const status = extractStatus(error);
    // nếu biết chắc status 401/403 -> logout
    if (status === 401 || status === 403) {
      // gọi logout logic (xoá storage)
      await logoutFn();
      // gọi logout public nếu muốn dùng react-admin flow:
      try {
        // @ts-ignore - gọi logout trên cùng object
        await authProvider.logout();
      } catch {
        // ignore
      }
      // fallback redirect nếu react-admin không chuyển
      forceRedirectToLogin();
      return Promise.reject(new Error("Unauthorized"));
    }

    return Promise.resolve();
  },

  checkAuth: async (_params?: any) => {
    const token = localStorage.getItem("token");
    return token
      ? Promise.resolve()
      : Promise.reject(new Error("Not authenticated"));
  },

  getPermissions: () => {
    const role = JSON.parse(localStorage.getItem("permissions") || "{}");
    return role ? Promise.resolve(role) : Promise.reject();
  },

  getIdentity: async (): Promise<UserIdentity> => {
    try {
      const resp = await userApi.usersCurrentUser();
      const identity = resp?.data;

      console.debug("[authProvider.getIdentity] identity:", identity);

      if (!identity) return Promise.reject(new Error("No identity returned"));
      localStorage.setItem("permissions", JSON.stringify(identity));

      if (identity.organization_id) {
        localStorage.setItem("organization_id", identity.organization_id);
      }

      if(identity.is_superuser)
      {
        localStorage.setItem("is_superuser", "true");
      }

      return identity as UserIdentity;
    } catch (err: any) {
      console.debug("[authProvider.getIdentity] error:", err);

      const status = extractStatus(err);
      if (status === 401) {
        await logoutFn();
        try {
          // @ts-ignore
          await authProvider.logout();
        } catch {
          // ignore
        }
        forceRedirectToLogin();
        return Promise.reject(new Error("Unauthorized"));
      }
      return Promise.reject(err);
    }
  },
};

export default authProvider;
